// Voice capture: record audio, upload it, get text back.
//
// This replaces the Web Speech API, which was three separate bugs in a trench
// coat — Android re-delivering already-sent results, the Gboard keyboard mic
// fighting React's controlled input, and no support at all inside a standalone
// iOS PWA. Recording bytes and transcribing them server-side behaves the same
// way on every platform.
//
// The tradeoff is honest: no live interim text. You see a timer while
// recording, and the transcript arrives when you stop.

export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  )
}

/** First container the browser will actually give us. Safari/iOS only does
 *  mp4; Chrome and Firefox prefer webm. Whisper accepts all of these. */
function pickMimeType(): string | undefined {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mpeg',
    'audio/ogg;codecs=opus',
  ]
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) return type
  }
  return undefined
}

export interface Recording {
  /** Stop recording and resolve the transcript. */
  stop(): Promise<string>
  /** Discard without transcribing. */
  cancel(): void
}

export interface RecorderOptions {
  /** Fired roughly once a second with elapsed seconds, for the UI timer. */
  onTick?: (seconds: number) => void
  /** Hard cap. Whisper bills per audio minute, so a forgotten open mic
   *  shouldn't run indefinitely. */
  maxSeconds?: number
  /** Called if the cap is hit, so the caller can update its own state. */
  onAutoStop?: () => void
}

export async function startRecording(opts: RecorderOptions = {}): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const mimeType = pickMimeType()
  const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
  const chunks: BlobPart[] = []
  let stopped = false

  rec.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data)
  }
  rec.start()

  const started = Date.now()
  const maxSeconds = opts.maxSeconds ?? 300

  const timer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - started) / 1000)
    opts.onTick?.(elapsed)
    if (elapsed >= maxSeconds && !stopped) opts.onAutoStop?.()
  }, 1000)

  const teardown = () => {
    clearInterval(timer)
    stream.getTracks().forEach((t) => t.stop()) // releases the mic indicator
  }

  const finished = new Promise<Blob>((resolve) => {
    rec.onstop = () => resolve(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }))
  })

  return {
    async stop() {
      if (stopped) return ''
      stopped = true
      if (rec.state !== 'inactive') rec.stop()
      const blob = await finished
      teardown()
      if (!blob.size) throw new Error('Nothing was recorded.')

      const res = await fetch('/api/transcribe', {
        method: 'POST',
        headers: { 'Content-Type': blob.type || 'application/octet-stream' },
        body: blob,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(
          (body && typeof body === 'object' && 'message' in body && String((body as any).message)) ||
            `Transcription failed (${res.status})`,
        )
      }
      const { text } = (await res.json()) as { text: string }
      return text
    },
    cancel() {
      if (stopped) return
      stopped = true
      if (rec.state !== 'inactive') rec.stop()
      teardown()
    },
  }
}
